import { BaseEdge, EdgeLabelRenderer, EdgeProps, getBezierPath, useReactFlow } from 'reactflow';
import { useCallback, useContext } from 'react';
import { Trash2 } from 'lucide-react';
import { IconButton } from '@chakra-ui/react';

import { brandColors, colors } from '@duke-hq/ui/theme/chakraTheme';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { ActiveNodeContext } from '../FlowEditorStateContext';
import { testIds as edgeTestIds } from './testIds';

// A centred glow, not a directional drop shadow, so it maps to no Chakra token.
const DELETE_BUTTON_HOVER_SHADOW = '0 0 6px 2px rgba(0, 0, 0, 0.08)';

export default function CustomEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
}: EdgeProps) {
  const { deleteElements } = useReactFlow();
  const activeNode = useContext(ActiveNodeContext);
  // A bezier curve rather than orthogonal (right-angle) routing: in a branching
  // flow the smooth curves are far easier to trace from a node to its target,
  // where stacked right-angle segments tend to overlap and blur together.
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Highlight the active node's connections so you can see at a glance where
  // the node you're editing leads, even in a large flow. A solid brand-coloured
  // stroke, not an animated dash, which reads as noise. Styled here at render
  // time, never on the edge objects themselves, which are serialised on save.
  const isConnectedToActiveNode =
    activeNode != null && (source === activeNode.id || target === activeNode.id);
  const edgeStyle = isConnectedToActiveNode
    ? {
        ...style,
        stroke: brandColors.brand[500],
        strokeWidth: 2.5,
      }
    : style;
  const onEdgeClick = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      evt.stopPropagation();
      deleteElements({ edges: [{ id }] });
    },
    [deleteElements],
  );

  //Show button only on hover
  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={edgeStyle} />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: 12,
            // everything inside EdgeLabelRenderer has no pointer events by default
            // if you have an interactive element, set pointer-events: all
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
        >
          <IconButton
            border="1px"
            bgColor="white"
            borderColor="border.default"
            size="xs"
            rounded="full"
            _hover={{ boxShadow: DELETE_BUTTON_HOVER_SHADOW }}
            onClick={(event) => onEdgeClick(event)}
            aria-label="remove edge"
            data-testid={edgeTestIds.deleteEdgeButton}
          >
            <Trash2 size={iconSize.xs} color={colors.edgeButtonIcon} />
          </IconButton>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
